import axios from 'axios';
import { setupCache } from 'axios-cache-interceptor';
import { getAuthToken } from './apiHelper';
import { baseUrlRes1 } from '../../constants/baseUrl';
import sessionManager from './sessionManager';
import cookieHelper from './cookieHelper';

// Create base axios instance
const baseAxios = axios.create({
  baseURL: baseUrlRes1
});

// Setup cache interceptor with shorter TTL for dynamic data
const axiosInstance = setupCache(baseAxios, {
  // Reduced TTL: 1 minute (was 5 minutes) to prevent stale data issues
  ttl: 1000 * 60 * 1,
  // Cache only GET requests by default
  methods: ['get'],
  // Don't cache auth-related endpoints
  cachePredicate: {
    statusCheck: (status) => status >= 200 && status < 400,
    requestMatch: (config) => {
      const url = config.url || '';
      // Don't cache auth, user data, or real-time data
      return !url.includes('/login') &&
             !url.includes('/validateStaffSession') &&
             !url.includes('/extendStaffSession') &&
             !url.includes('/userDetails') &&
             !url.includes('web/users/') &&
             !url.includes('/getLastSession') &&
             !url.includes('/staffDetails') &&
             !url.includes('/permissions/') &&
             !url.includes('/status-counts') &&
             !url.includes('/status-hierarchy') &&
             !url.includes('/sleep/orders') &&
             !url.includes('/sleep/patients') &&
             !url.includes('/sleep/users');
    }
  }
});

axiosInstance.invalidateCacheEntries = async (cacheIds = []) => {
  if (!axiosInstance.storage) {
    return;
  }

  if (Array.isArray(cacheIds) && cacheIds.length > 0) {
    await Promise.all(cacheIds.map(cacheId => axiosInstance.storage.remove(cacheId)));
  }
};

// ── Mutation-aware cache bypass (race-condition-free) ──────────────
// After any real mutation we record the timestamp synchronously.
// The request interceptor checks this and forces `cache: false` on
// GETs that happen within the bypass window so they always hit the
// server. This avoids the async race that storage.clear() causes
// when in-flight GETs re-cache stale data after the clear.
let lastMutationTs = 0;
const CACHE_BYPASS_MS = 2000; // bypass cache for 2s after any mutation

// POST endpoints that are read-only (should NOT trigger cache bypass)
const READ_ONLY_POSTS = [
  '/stafflogin',
  '/validateStaffSession',
  '/endStaffSession',
  '/extendStaffSession',
  '/permissions/check',
  '/work-order-notes/search',
  '/check-duplicate',
  '/forgotStaffPassword',
  '/resetStaffPassword',
];

// Flag to prevent multiple simultaneous refresh attempts
let isRefreshing = false;
let failedQueue = [];
let isRedirecting = false; // Flag to prevent multiple redirects

const processQueue = (error, token = null) => {
  failedQueue.forEach(prom => {
    if (error) {
      prom.reject(error);
    } else {
      prom.resolve(token);
    }
  });

  failedQueue = [];
};

// Helper function to get staffId from token (even if expired)
const getStaffIdFromToken = (token) => {
  try {
    if (!token) return null;
    const base64Url = token.split('.')[1];
    const base64 = base64Url.replace(/-/g, '+').replace(/_/g, '/');
    const jsonPayload = decodeURIComponent(
      atob(base64)
        .split('')
        .map(c => `%${('00' + c.charCodeAt(0).toString(16)).slice(-2)}`)
        .join('')
    );
    const payload = JSON.parse(jsonPayload);
    return payload.staffId;
  } catch (error) {
    return null;
  }
};

// Function to refresh the access token
const refreshAccessToken = async () => {
  try {
    const currentToken = cookieHelper.getToken() || getAuthToken();
    if (!currentToken) {
      throw new Error('No token available');
    }

    const staffId = getStaffIdFromToken(currentToken);
    if (!staffId) {
      throw new Error('Could not extract staffId from token');
    }

    // Step 1: Validate session and get refresh token
    // Use raw axios with timeout to prevent hanging requests
    const validateResponse = await Promise.race([
      axios.post(
        `${baseUrlRes1}api/v1/validateStaffSession`,
        { staffId },
        {
          headers: {
            'Content-Type': 'application/json',
            // Don't include Authorization header for this request
          },
          timeout: 10000, // 10 second timeout
        }
      ),
      new Promise((_, reject) => 
        setTimeout(() => reject(new Error('Session validation timeout')), 10000)
      )
    ]);

    if (!validateResponse.data?.valid || !validateResponse.data?.refreshToken) {
      throw new Error('Session validation failed');
    }

    // Step 2: Extend session with refresh token to get new access token
    const extendResponse = await Promise.race([
      axios.post(
        `${baseUrlRes1}api/v1/extendStaffSession`,
        {
          staffId,
          refreshToken: validateResponse.data.refreshToken
        },
        {
          headers: {
            'Content-Type': 'application/json',
          },
          timeout: 10000, // 10 second timeout
        }
      ),
      new Promise((_, reject) => 
        setTimeout(() => reject(new Error('Token extension timeout')), 10000)
      )
    ]);

    if (!extendResponse.data?.success || !extendResponse.data?.accessToken) {
      throw new Error('Token refresh failed');
    }

    // Update token in cookie and all localStorage keys so logout/login-as and permissions see the same token
    cookieHelper.setToken(extendResponse.data.accessToken);
    // localStorage.setItem('id_token', extendResponse.data.accessToken);
    // localStorage.setItem('@token', extendResponse.data.accessToken);

    // COMMENTED OUT: Proactive expiry tracking disabled (staff issues). 401 extend remains.
    // if (sessionManager && typeof sessionManager.setupExpiryTracking === 'function') {
    //   sessionManager.setupExpiryTracking();
    // }

    return extendResponse.data.accessToken;
  } catch (error) {
    // Log error for debugging but don't expose sensitive info
    console.error('Token refresh error:', error.message || 'Unknown error');
    throw error;
  }
};

// Auth endpoints that must never receive an Authorization header —
// sending a stale token here causes the server to error before CORS headers are set.
const NO_AUTH_ENDPOINTS = [
  '/stafflogin',
  '/login',
  '/signin',
  '/forgotStaffPassword',
  '/resetStaffPassword',
  '/azure-login',
];

// Add request interceptor to add auth token to all requests
axiosInstance.interceptors.request.use(
  config => {
    const url = config.url || '';
    const isAuthEndpoint = NO_AUTH_ENDPOINTS.some(p => url.includes(p));

    if (!isAuthEndpoint) {
      const token = cookieHelper.getToken() || getAuthToken();
      if (token) {
        config.headers['Authorization'] = `Bearer ${token}`;
      }
    }

    // Bypass cache for GETs that happen right after a mutation
    const method = (config.method || '').toLowerCase();
    if (method === 'get' && Date.now() - lastMutationTs < CACHE_BYPASS_MS) {
      config.cache = false;
    }

    // Explicitly remove headers that might trigger CORS preflight issues
    delete config.headers['Expires'];
    delete config.headers['Cache-Control'];
    delete config.headers['Pragma'];

    return config;
  },
  error => {
    return Promise.reject(error);
  }
);

// Add response interceptor to handle common errors and token refresh
axiosInstance.interceptors.response.use(
  (response) => {
    // After a real mutation succeeds, record the timestamp (synchronous, no race)
    const method = (response.config.method || '').toLowerCase();
    if (['post', 'put', 'patch', 'delete'].includes(method)) {
      const url = response.config.url || '';
      const isReadLike = READ_ONLY_POSTS.some(p => url.includes(p));
      if (!isReadLike) {
        lastMutationTs = Date.now();
      }
    }
    return response;
  },
  async error => {
    const originalRequest = error.config;

    // If error is 401 and we haven't already tried to refresh
    if (error.response && error.response.status === 401 && !originalRequest._retry) {
      // Skip token refresh for login/authentication endpoints
      const isAuthEndpoint = originalRequest.url?.includes('/login') ||
        originalRequest.url?.includes('/signin') ||
        originalRequest.url?.includes('/validateStaffSession') ||
        originalRequest.url?.includes('/extendStaffSession');

      if (isAuthEndpoint) {
        return Promise.reject(error);
      }

      // Check if we're already on login page to prevent redirect loops
      const currentPath = window.location.pathname;
      const isOnLoginPage = currentPath === '/signin' || currentPath === '/signin/' || currentPath === '/';

      if (isOnLoginPage) {
        // Already on login page, don't try to refresh or redirect
        return Promise.reject(error);
      }

      if (isRefreshing) {
        // If already refreshing, queue this request
        return new Promise((resolve, reject) => {
          failedQueue.push({ resolve, reject });
        })
          .then(token => {
            if (token) {
              originalRequest.headers['Authorization'] = `Bearer ${token}`;
              return axiosInstance(originalRequest);
            } else {
              return Promise.reject(new Error('Token refresh failed'));
            }
          })
          .catch(err => {
            return Promise.reject(err);
          });
      }

      originalRequest._retry = true;
      isRefreshing = true;

      try {
        const newToken = await refreshAccessToken();
        processQueue(null, newToken);

        // Retry the original request with new token
        originalRequest.headers['Authorization'] = `Bearer ${newToken}`;
        // Note: axios-cache-interceptor might wrap the request, so we use axiosInstance
        return axiosInstance(originalRequest);
      } catch (refreshError) {
        processQueue(refreshError, null);

        // Only redirect if not already on login page and not already redirecting
        if (!isOnLoginPage && !isRedirecting) {
          isRedirecting = true;
          // Small delay to prevent multiple simultaneous redirects
          setTimeout(() => {
            const stillOnPage = window.location.pathname !== '/signin' && 
                               window.location.pathname !== '/signin/' &&
                               window.location.pathname !== '/';
            if (stillOnPage && isRedirecting) {
              sessionManager.redirectToLogin('Your session has expired. Please log in again.');
            }
            // Reset flag after redirect attempt
            setTimeout(() => {
              isRedirecting = false;
            }, 1000);
          }, 100);
        }
        
        return Promise.reject(refreshError);
      } finally {
        isRefreshing = false;
      }
    }

    return Promise.reject(error);
  }
);

export default axiosInstance;
